import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import { removePrefixFromString } from './util';
import moment from 'moment';

// article types:
export const MACRO = 'MACRO';
export const KB = 'KB';

// macro types
export const EMAIL = 'EMAIL';
export const CHAT = 'CHAT';

// prefixes on algolia objectIDs
const KB_ARTICLE_PREFIX = 'PARSE_KB_ARTICLE_';
const MACRO_PREFIX = 'PARSE_MACRO_';

export const KB_EDITABLE_FIELDS = ['fullSubheading', 'category', 'brand', 'heading', 'text'];

export const MACRO_EDITABLE_FIELDS = [
  'fullSubheading',
  'category',
  'brand',
  'description',
  'keywords',
  'keywords_low',
  'link',
  'dummyButtonActionTag',
  'couponCodeType',
  'expirationDate',
  'emailText',
  'chatText',
  'instructions',
];

export const KB_MACRO_LABEL_OVERRIDE = {
  fullSubheading: 'title',
  emailText: 'Email Version',
  chatText: 'Chat Version',
  dummyButtonActionTag: 'Dummy Button Action Tag',
};

export const MACRO_WIZARD_OVERRIDE = {
  category: 'tags',
};

export const DEFAULT_ARTICLE_TITLE = 'Untitled article';

export const getFieldsForArticleType = (articleType, isNew = false) => {
  // save editable fields to parse
  let fields;
  if (articleType === KB) {
    fields = KB_EDITABLE_FIELDS;
  } else if (articleType === MACRO) {
    fields = MACRO_EDITABLE_FIELDS;
    // only add the couponCodeType type for new fields
    if (isNew) {
      fields.push('couponCodeType');
    }
  } else {
    throw new Error('Cannot save article for unrecognized article type');
  }
  // add macro wizard fields
  fields = fields.concat(Object.values(MACRO_WIZARD_OVERRIDE));

  return fields;
};

export const extractKnowledgeBaseArticleId = (algoliaObjectId) => {
  return removePrefixFromString(algoliaObjectId, KB_ARTICLE_PREFIX);
};
export const extractMacroArticleId = (algoliaObjectId) => {
  return removePrefixFromString(algoliaObjectId, MACRO_PREFIX);
};

export const processMacroArticle = (macroArticle) => {
  const text = macroArticle.text;
  let chatText = macroArticle.chatText;
  let emailText = macroArticle.emailText;
  // initailize chat and email text if not set
  if (!chatText && !emailText) {
    emailText = text;
    chatText = text;
  }
  const expirationDate = macroArticle.expirationDate;

  return {
    id: `${MACRO}_${macroArticle.objectId}`,
    objectId: macroArticle.objectId,
    simplrTitle: macroArticle.fullSubheading,
    articleType: MACRO,
    createdAt: macroArticle.createdAt,
    updatedAt: macroArticle.updatedAt,
    authorEmail: macroArticle.author?.email,
    authorSimplrExpertId: macroArticle.author?.objectId || macroArticle.authorId,
    updaterEmail: macroArticle.updater?.email,
    simplrAccountId: macroArticle.simplrAccountPtr?.objectId || macroArticle.simplrAccountId,
    fullSubheading: macroArticle.fullSubheading,
    description: macroArticle.description,
    category: macroArticle.category,
    text,
    emailText,
    chatText,
    articleNumber: macroArticle.articleNumber,
    keywords: macroArticle.keywords,
    keywords_low: macroArticle.keywords_low,
    link: macroArticle.link,
    subheading1: macroArticle.subheading1,
    subheading2: macroArticle.subheading2,
    couponCodeTypeId: macroArticle.couponCodeTypePtr?.objectId || macroArticle.couponCodeTypeId,
    tags: macroArticle.tags,
    expirationDate: expirationDate && moment(expirationDate),
    instructions: macroArticle.instructions,
    brand: macroArticle.brand,
    dummyButtonActionTag: macroArticle.dummyButtonActionTag,
    macroType: macroArticle.macroType || null,
    decisionTreeTags: macroArticle.decisionTreeTags,
    dummyButtonActionTags: macroArticle.dummyButtonActionTags,
  };
};

export const getKbOrMacroResultSnippet = (algoliaResult) => {
  return get(algoliaResult, '_snippetResult.text.value', null);
};

export const getHistoricalTicketSnippetIndex = (algoliaResult) => {
  let snippetIndex = findIndex(algoliaResult._snippetResult.messages, (snippetMessage) => {
    return snippetMessage.matchLevel === 'full';
  });
  if (snippetIndex === -1) {
    snippetIndex = findIndex(algoliaResult._snippetResult.messages, (snippetMessage) => {
      return snippetMessage.matchLevel === 'partial';
    });
  }
  if (snippetIndex === -1) {
    snippetIndex = findIndex(algoliaResult._snippetResult.subjects, (snippetSubject) => {
      return snippetSubject.matchLevel === 'full';
    });
  }
  if (snippetIndex === -1) {
    snippetIndex = findIndex(algoliaResult._snippetResult.subjects, (snippetSubject) => {
      return snippetSubject.matchLevel === 'partial';
    });
  }

  return snippetIndex;
};

export const getHistoricalTicketMessageSnippet = (algoliaResult, snippetIndex) => {
  return get(algoliaResult, `_snippetResult.messages[${snippetIndex}].value`, null);
};

export const getHistoricalTicketSubjectSnippet = (algoliaResult, snippetIndex) => {
  return get(algoliaResult, `_snippetResult.subjects[${snippetIndex}].value`, null);
};

export const getAlgoliaResultHits = (results) => {
  return results && results.hits;
};

/*
Replace images and image links in these formats:
image tags: <img src=...>
image links starting with http(s)
image links in this markup: !()[]
image links surrounded in brackets
*/
export const filterImgLinksfromString = (str) => {
  const imgTagsAndLinksRegex =
    /(<[\s]*img[^>]* src=["']([^"']*)["'][^>]*>)|!\[[^\]]*\]\((([ "']*https?:\/\/[^\n \])]*(\.png|\.jpg|\.jpeg|\.gif|\.svg|\.bmp|\.tiff|\.tif)["']*[^\n \])]*))*[)]*|(https?:\/\/[^\n \])]*(\.png|\.jpg|\.jpeg|\.gif|\.svg|\.bmp|\.tiff|\.tif)["']*[^\n \])]*)|\[(https?:\/\/[^\n \])]*(\.png|\.jpg|\.jpeg|\.gif|\.svg|\.bmp|\.tiff|\.tif)["']*[^\n \])]*)\]/gi;

  return str.replace(imgTagsAndLinksRegex, ' [Link to Image Removed] ');
};
